import { useReducer, useEffect } from 'react';

import useDropdown from '../usedropdown';
import useContractManagement from '../contractmanagement/hook';
import contractManagementService from '../../../services/contractmanagementservice';

const ACTION_TYPES = {
  INIT_REQUEST: 'INIT_REQUEST',
  INIT_SUCCESS: 'INIT_SUCCESS',
  INIT_FAILURE: 'INIT_FAILURE',
  SELECT_CONTRACT_REQUEST: 'SELECT_CONTRACT_REQUEST',
  SELECT_CONTRACT_SUCCESS: 'SELECT_CONTRACT_SUCCESS',
  SELECT_CONTRACT_FAILURE: 'SELECT_CONTRACT_FAILURE',
  OPEN_AFFECTED_ENCOUNTERS_DIALOG: 'OPEN_AFFECTED_ENCOUNTERS_DIALOG',
  CLOSE_AFFECTED_ENCOUNTERS_DIALOG: 'CLOSE_AFFECTED_ENCOUNTERS_DIALOG'
};

const ACTIONS = {
  initRequest: () => ({
    type: ACTION_TYPES.INIT_REQUEST
  }),
  initSuccess: result => ({
    type: ACTION_TYPES.INIT_SUCCESS,
    payload: { ...result }
  }),
  initFailure: error => ({
    type: ACTION_TYPES.INIT_FAILURE,
    error
  }),
  selectContractRequest: () => ({
    type: ACTION_TYPES.SELECT_CONTRACT_REQUEST
  }),
  selectContractSuccess: () => ({
    type: ACTION_TYPES.SELECT_CONTRACT_SUCCESS
  }),
  selectContractFailure: error => ({
    type: ACTION_TYPES.SELECT_CONTRACT_FAILURE,
    error
  }),
  openAffectedEncountersDialog: (
    contractId,
    affectedEncounterCount,
    auditType,
    itemId
  ) => ({
    type: ACTION_TYPES.OPEN_AFFECTED_ENCOUNTERS_DIALOG,
    payload: {
      contractId,
      affectedEncounterCount,
      auditType,
      itemId
    }
  }),
  closeAffectedEncountersDialog: () => ({
    type: ACTION_TYPES.CLOSE_AFFECTED_ENCOUNTERS_DIALOG
  })
};

const initialState = {
  isLoading: false,
  loadingMessage: '',
  affectedEncountersDialog: {
    isOpen: false,
    affectedEncounterCount: 0,
    auditType: '',
    contractId: -1,
    itemId: null
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.INIT_REQUEST: {
      return {
        ...state,
        isLoading: true,
        loadingMessage: 'Initializing page'
      };
    }

    case ACTION_TYPES.INIT_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        loadingMessage: ''
      };
    }

    case ACTION_TYPES.INIT_FAILURE: {
      return {
        ...state,
        isLoading: false,
        isFailedInit: true,
        loadingMessage: ''
      };
    }

    case ACTION_TYPES.SELECT_CONTRACT_REQUEST: {
      return {
        ...state,
        isLoading: true,
        loadingMessage: 'Getting contract settings'
      };
    }

    case ACTION_TYPES.SELECT_CONTRACT_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        loadingMessage: ''
      };
    }

    case ACTION_TYPES.SELECT_CONTRACT_FAILURE: {
      return {
        ...state,
        isLoading: false,
        loadingMessage: ''
      };
    }

    case ACTION_TYPES.OPEN_AFFECTED_ENCOUNTERS_DIALOG: {
      const {
        payload: {
          contractId,
          affectedEncounterCount,
          auditType,
          itemId
        }
      } = action;

      return {
        ...state,
        affectedEncountersDialog: {
          ...state.affectedEncountersDialog,
          isOpen: true,
          contractId,
          affectedEncounterCount,
          auditType,
          itemId
        }
      };
    }

    case ACTION_TYPES.CLOSE_AFFECTED_ENCOUNTERS_DIALOG: {
      return {
        ...state,
        affectedEncountersDialog: {
          ...initialState.affectedEncountersDialog
        }
      };
    }

    default: {
      throw new Error('contractManagementPage reducer default should never hit');
    }
  }
};

const useContractManagementPage = service => {
  const contractService = contractManagementService(service.serviceRoutes);

  const [contractDropdownState, contractDropdownActions] = useDropdown();

  const [state, dispatch] = useReducer(reducer, initialState);
  const [
    managementState,
    managementActions
  ] = useContractManagement(contractService);

  const selectContract = e => {
    const contractId = e.target.value;
    contractDropdownActions.setSelectedItem(e);

    if (contractId === '-1') {
      managementActions.hydrate(null);
      return;
    }

    dispatch(ACTIONS.selectContractRequest());

    service.selectContract(contractId)
    .then(contractSettingsData => {
      dispatch(ACTIONS.selectContractSuccess());

      // hydrate managementState with contractSettingsData
      managementActions.hydrate(contractSettingsData);
    })
    .catch(e => {
      dispatch(ACTIONS.selectContractFailure(e.toString()));

      managementActions.hydrate(null);

      const noop = () => {};
      const fakeEvent = {
        target: { value: '-1' },
        stopPropagation: noop,
        preventDefault: noop
      };

      contractDropdownActions.setSelectedItem(fakeEvent);
    });
  };

  const openAffectedEncountersDialog = (
    contractId,
    affectedEncounterCount,
    auditType,
    itemId
  ) => {
    dispatch(ACTIONS.openAffectedEncountersDialog(
      contractId,
      affectedEncounterCount,
      auditType,
      itemId
    ));
  };

  const closeAffectedEncountersDialog = () => {
    dispatch(ACTIONS.closeAffectedEncountersDialog());
  };

  const disableNewHCCAudit = (contractId) => {
    return managementActions.disableNewHCCAudit(
      contractId,
      openAffectedEncountersDialog
    );
  };

  const disableUnvalidatedDxAudit = contractId => {
    return managementActions.disableUnvalidatedDxAudit(
      contractId,
      openAffectedEncountersDialog
    );
  };

  const disableCoderAudit = contractId => {
    return managementActions.disableCoderAudit(
      contractId,
      openAffectedEncountersDialog
    );
  };

  const disableCCAudit = contractId => {
    return managementActions.disableCCAudit(
      contractId,
      openAffectedEncountersDialog
    );
  };

  const disableDxAudit = contractId => {
    return managementActions.disableDxAudit(
      contractId,
      openAffectedEncountersDialog
    );
  };

  const removeAuditReasons = (contractId, auditType, itemId) => {
    return managementActions.removeAuditReasons(contractId, auditType, itemId)
      .then(() => {
        closeAffectedEncountersDialog();
      });
  };

  const enableClientReview = contractId => {
    return managementActions.enableClientReview(contractId);
  };

  const disableClientReview = contractId => {
    return managementActions.disableClientReview(contractId);
  };

  const boundActions = {
    openAffectedEncountersDialog,
    closeAffectedEncountersDialog,
    selectContract,
    getAffectedEncounters: managementActions.getAffectedEncounters,
    enableAutoExport: managementActions.enableAutoExport,
    disableAutoExport: managementActions.disableAutoExport,
    enablePurgeDuringNextRead: managementActions.enablePurgeDuringNextRead,
    disablePurgeDuringNextRead: managementActions.disablePurgeDuringNextRead,
    enableDocProcAutofinalize: managementActions.enableDocProcAutofinalize,
    disableDocProcAutofinalize: managementActions.disableDocProcAutofinalize,
    enableAutofinalizedEncounterDocProcRead:
      managementActions.enableAutofinalizedEncounterDocProcRead,
    disableAutofinalizedEncounterDocProcRead:
      managementActions.disableAutofinalizedEncounterDocProcRead,
    enableCoderAudit: managementActions.enableCoderAudit,
    disableCoderAudit: disableCoderAudit,
    enableNewHCCAudit: managementActions.enableNewHCCAudit,
    disableNewHCCAudit,
    enableUnvalidatedDxAudit: managementActions.enableUnvalidatedDxAudit,
    disableUnvalidatedDxAudit,
    enableCCAudit: managementActions.enableCCAudit,
    disableCCAudit,
    enableDxAudit: managementActions.enableDxAudit,
    disableDxAudit,
    placeDxOnAudit: managementActions.placeDxOnAudit,
    removeDxFromAudit: managementActions.removeDxFromAudit,
    placeCCOnAudit: managementActions.placeCCOnAudit,
    removeCCFromAudit: managementActions.removeCCFromAudit,
    placeCoderOnAudit: managementActions.placeCoderOnAudit,
    removeCoderFromAudit: managementActions.removeCoderFromAudit,
    removeAuditReasons,
    manualAutofinalize: managementActions.manualAutofinalize,
    purgeEncounters: managementActions.purgeEncounters,
    changeWorkByPriority: managementActions.changeWorkByPriority,
    changeStrategyPriority: managementActions.changeStrategyPriority,
    priorityWorkByActions: managementActions.priorityWorkByActions,
    priorityStrategyActions: managementActions.priorityStrategyActions,
    enableAutoReread: managementActions.enableEncounterAutoReread,
    disableAutoReread: managementActions.disableEncounterAutoReread,
    enableClientReview,
    disableClientReview
  };

  // init
  useEffect(() => {
    dispatch(ACTIONS.initRequest());

    service.init()
      .then(responseJson => {
        const contracts = responseJson;

        dispatch(ACTIONS.initSuccess(responseJson));

        // hydrate
        contractDropdownActions.setItems(contracts);
      })
      .catch(e => dispatch(ACTIONS.initFailure(e.toString())));
  }, []);

  const getLoadingMessage = () => {
    if (managementState.isLoading) return managementState.loadingMessage;

    return state.loadingMessage;
  };

  const isPageLoading = (
    state.isLoading
    || managementState.isLoading
  );

  return [
    {
      ...state,
      isLoading: isPageLoading,
      loadingMessage: getLoadingMessage(),
      contractDropdown: { ...contractDropdownState },
      management: { ...managementState }
    },
    boundActions
  ];
};

export default useContractManagementPage;
